import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getInvoices } from '@/modules/Invoice/services'
import { INVOICE_STATUS_OPTIONS } from '@/modules/Invoice/constants'
import { GetInvoicesRow } from '@/modules/Invoice/types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Typogaphy } from 'vnetwork-ui'
import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'
import { useState, useMemo } from 'react'
import { FileText, Receipt, CreditCard, ArrowRight } from 'lucide-react'
import { formatCurrency } from '@/utils/function'

type TabType = 'waiting-confirm' | 'approved'

const InvoiceWaitingWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<TabType>('waiting-confirm')

    // Fetch invoices waiting confirm
    const { data: waitingData, isLoading: isWaitingLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.INVOICES, 'waiting-confirm-dashboard'],
            queryFn: () =>
                getInvoices({
                    page: 1,
                    page_size: 10,
                    q: '',
                    filters: btoa(
                        JSON.stringify({
                            invoice_status: [
                                INVOICE_STATUS_OPTIONS.WaitingConfirm,
                            ],
                        })
                    ),
                }),
        })

    // Fetch invoices approved (công nợ)
    const { data: approvedData, isLoading: isApprovedLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.INVOICES, 'approved-dashboard'],
            queryFn: () =>
                getInvoices({
                    page: 1,
                    page_size: 10,
                    q: '',
                    filters: btoa(
                        JSON.stringify({
                            invoice_status: [INVOICE_STATUS_OPTIONS.Approved],
                        })
                    ),
                }),
        })

    const isLoading = isWaitingLoading || isApprovedLoading

    const waitingInvoices = waitingData?.results || []
    const approvedInvoices = approvedData?.results || []

    const tabs = useMemo(
        () => [
            {
                key: 'waiting-confirm' as TabType,
                label: t('widgets.invoices.waiting_confirm', 'Chờ xác nhận'),
                count: waitingData?.pagination?.total || 0,
            },
            {
                key: 'approved' as TabType,
                label: t('widgets.invoices.approved', 'Công nợ'),
                count: approvedData?.pagination?.total || 0,
            },
        ],
        [waitingData?.pagination?.total, approvedData?.pagination?.total, t]
    )

    const currentList =
        activeTab === 'waiting-confirm' ? waitingInvoices : approvedInvoices
    const currentTotal =
        activeTab === 'waiting-confirm'
            ? waitingData?.pagination?.total || 0
            : approvedData?.pagination?.total || 0

    const handleInvoiceClick = (invoiceId: number) => {
        navigate(
            generatePathDatacentral(paths.invoiceDetail, { id: invoiceId })
        )
    }

    const handleViewAll = () => {
        if (activeTab === 'waiting-confirm') {
            navigate(
                generatePathDatacentral(
                    paths.invoices,
                    {},
                    {
                        filters: btoa(
                            JSON.stringify({
                                invoice_status: [
                                    INVOICE_STATUS_OPTIONS.WaitingConfirm,
                                ],
                            })
                        ),
                    }
                )
            )
        } else {
            navigate(
                generatePathDatacentral(
                    paths.invoices,
                    {},
                    {
                        filters: btoa(
                            JSON.stringify({
                                invoice_status: [
                                    INVOICE_STATUS_OPTIONS.Approved,
                                ],
                            })
                        ),
                    }
                )
            )
        }
    }

    const renderInvoiceItem = (invoice: GetInvoicesRow) => {
        const isApproved = activeTab === 'approved'

        return (
            <div
                key={invoice.invoice_id}
                onClick={() => handleInvoiceClick(invoice.invoice_id)}
                className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                        className={`p-1.5 rounded-full flex-shrink-0 ${isApproved ? 'bg-blue-100' : 'bg-orange-100'}`}
                    >
                        {isApproved ? (
                            <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                        ) : (
                            <Receipt className="w-3.5 h-3.5 text-orange-600" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-gray-900 truncate">
                            {invoice.customer?.customer_name ||
                                invoice.customer?.company_name}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                            {formatCurrency(invoice.invoice_total)}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                            isApproved
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-orange-100 text-orange-700'
                        }`}
                    >
                        {isApproved
                            ? t('widgets.invoices.debt', 'Công nợ')
                            : t('widgets.waiting', 'Chờ xác nhận')}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                </div>
            </div>
        )
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Title */}
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-500" />
                    <Typogaphy className="font-semibold text-primary">
                        {t('widgets.invoices.title', 'Hoá đơn')}
                    </Typogaphy>
                </div>

                {/* Tabs */}
                <div className="flex-shrink-0 mb-3" data-tour="invoice-tabs">
                    <ButtonGroupCustomized
                        className="p-0.5"
                        active={activeTab}
                        onChange={(value) => setActiveTab(value as TabType)}
                        buttons={tabs.map((tab) => ({
                            label: tab.label,
                            value: tab.key,
                            count: tab.count,
                        }))}
                    />
                </div>

                {isLoading ? (
                    <div className="space-y-2 flex-1">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-14 rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Summary */}
                        <div
                            className={`flex items-center gap-2 p-2.5 rounded-lg mb-3 flex-shrink-0 ${
                                activeTab === 'approved'
                                    ? 'bg-blue-50'
                                    : 'bg-orange-50'
                            }`}
                            data-tour={
                                activeTab === 'approved'
                                    ? 'invoice-debt-summary'
                                    : 'invoice-waiting-summary'
                            }
                        >
                            {activeTab === 'approved' ? (
                                <CreditCard className="w-4 h-4 text-blue-600" />
                            ) : (
                                <Receipt className="w-4 h-4 text-orange-600" />
                            )}
                            <div>
                                <div
                                    className={`text-lg font-bold ${
                                        activeTab === 'approved'
                                            ? 'text-blue-600'
                                            : 'text-orange-600'
                                    }`}
                                >
                                    {currentTotal}
                                </div>
                                <div
                                    className={`text-[10px] ${
                                        activeTab === 'approved'
                                            ? 'text-blue-600'
                                            : 'text-orange-600'
                                    }`}
                                >
                                    {activeTab === 'waiting-confirm'
                                        ? t(
                                              'widgets.invoices.invoices_waiting',
                                              'Hoá đơn chờ xác nhận'
                                          )
                                        : t(
                                              'widgets.invoices.invoices_debt',
                                              'Hoá đơn công nợ'
                                          )}
                                </div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="space-y-1.5 flex-1">
                            {currentList.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                    {t(
                                        'widgets.invoices.no_invoices',
                                        'Không có hoá đơn'
                                    )}
                                </div>
                            ) : (
                                <>
                                    {currentList
                                        .slice(0, 3)
                                        .map((invoice) =>
                                            renderInvoiceItem(invoice)
                                        )}
                                    {/* Empty placeholders */}
                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            3 - currentList.length
                                        ),
                                    }).map((_, i) => (
                                        <div
                                            key={`empty-${i}`}
                                            className="h-[52px] bg-gray-50 rounded-lg border border-dashed border-gray-200"
                                        />
                                    ))}
                                </>
                            )}
                        </div>

                        {/* View All */}
                        {currentTotal > 0 && (
                            <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                                <button
                                    onClick={handleViewAll}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {t('widgets.view_all')} ({currentTotal})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Paper>
    )
}

export default InvoiceWaitingWidget
